import { createContext, useCallback, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import Alert from '@mui/material/Alert'
import Snackbar from '@mui/material/Snackbar'
import styled from 'styled-components'
import NotificationToast from './NotificationToast'

export type AppToastSeverity = 'success' | 'error' | 'warning' | 'info'

interface AppToastContextValue {
  showToast: (message: string, severity?: AppToastSeverity) => void
  showNotificationToast: (title: string, body: string) => void
}

interface ToastState {
  open: boolean
  message: string
  severity: AppToastSeverity
}

const DEFAULT_TOAST: ToastState = {
  open: false,
  message: '',
  severity: 'info',
}

export const AppToastContext = createContext<AppToastContextValue | null>(null)

const StyledAlert = styled(Alert)`
  && {
    color: #ffffff;
  }

  &&.MuiAlert-filledSuccess {
    background-color: #2a5caf;
  }

  &&.MuiAlert-filledInfo {
    background-color: #6366f1;
  }
`

export const AppToastProvider = ({ children }: { children: ReactNode }): React.JSX.Element => {
  const [toast, setToast] = useState<ToastState>(DEFAULT_TOAST)
  const [notifToast, setNotifToast] = useState({ open: false, title: '', body: '' })

  const showToast = useCallback((message: string, severity: AppToastSeverity = 'info') => {
    setToast({ open: true, message, severity })
  }, [])

  const showNotificationToast = useCallback((title: string, body: string) => {
    setNotifToast({ open: true, title, body })
  }, [])

  const handleClose = useCallback(() => {
    setToast((prev) => ({ ...prev, open: false }))
  }, [])

  const handleNotifClose = useCallback(() => {
    setNotifToast((prev) => ({ ...prev, open: false }))
  }, [])

  const value = useMemo<AppToastContextValue>(
    () => ({ showToast, showNotificationToast }),
    [showToast, showNotificationToast],
  )

  return (
    <AppToastContext.Provider value={value}>
      {children}
      <Snackbar
        open={toast.open}
        autoHideDuration={3500}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <StyledAlert onClose={handleClose} severity={toast.severity} variant="filled">
          {toast.message}
        </StyledAlert>
      </Snackbar>
      <NotificationToast
        open={notifToast.open}
        title={notifToast.title}
        body={notifToast.body}
        onClose={handleNotifClose}
      />
    </AppToastContext.Provider>
  )
}
